/**
 * Deskryptory kolumn tabeli dodatkowej — most między definicją pól tabeli dodatkowej
 * (`AdditionalField`) a kontraktem układu kolumn tabel osadzonych (`@saas/shared`
 * `embedded-table-columns`). Używają go zarówno renderer zakładki na karcie rekordu
 * (`AdditionalTableTab`), jak i edytor układu kolumn w kreatorze formatki.
 */

import {
  type EmbeddedTableColumnDescriptor,
  type EmbeddedTableColumnKind,
  SYSTEM_EXT_FIELD_TYPE_SET,
  type SystemExtFieldType,
} from '@saas/shared';

import { normalizeDictionaryParamBinding } from '@/hooks/useDictionary';
import type { AdditionalField } from '@/lib/additional-tables/types';

/**
 * Rodzaj danych kolumny z typu pola tabeli dodatkowej (całkowite pokrycie unii typów —
 * nowy typ w `SYSTEM_EXT_FIELD_TYPES` nie skompiluje się, dopóki nie dostanie rodzaju):
 * `numeric` → dziesiętna (liczba miejsc po przecinku), `integer` → całkowita, daty
 * (`date`, `timestamp`) → data, `boolean` → logiczna; reszta (tekst, e-mail, URL, listy,
 * odwołania, pliki) to zwykły tekst.
 */
const KIND_BY_TYPE: Record<SystemExtFieldType, EmbeddedTableColumnKind> = {
  varchar: 'text',
  text: 'text',
  integer: 'integer',
  numeric: 'decimal',
  boolean: 'boolean',
  date: 'date',
  timestamp: 'date',
  select: 'text',
  multiselect: 'text',
  email: 'text',
  url: 'text',
  reference: 'text',
  file: 'text',
};

function isSystemExtFieldType(value: string): value is SystemExtFieldType {
  return SYSTEM_EXT_FIELD_TYPE_SET.has(value);
}

/**
 * Rodzaj kolumny; nieznany (starszy) typ pola z bazy → zwykły tekst. Eksportowane, bo tę samą
 * regułę stosują kolumny pól dodatkowych pozycji dokumentu (`document-items-descriptors`).
 */
export function columnKindOf(fieldType: string): EmbeddedTableColumnKind {
  return isSystemExtFieldType(fieldType) ? KIND_BY_TYPE[fieldType] : 'text';
}

/**
 * Slugi pól tabeli, z których wiązania `record_field` (parametry słowników) INNYCH pól czytają
 * wartość w tym samym wierszu. Ukrycie takiej kolumny odcięłoby słownikowi parametr (lista
 * opcji w wierszu dodawania i w edycji komórki nie miałaby skąd go wziąć), więc jest zablokowana.
 */
function recordFieldDependencySlugs(fields: readonly AdditionalField[]): Set<string> {
  const slugs = new Set<string>();
  for (const field of fields) {
    for (const raw of Object.values(field.dictionaryParamBindings ?? {})) {
      const binding = normalizeDictionaryParamBinding(raw);
      if (binding?.kind === 'record_field') slugs.add(binding.fieldSlug);
    }
  }
  return slugs;
}

/**
 * Deskryptory kolumn dla pól tabeli dodatkowej, w kolejności `sortOrder` (ta sama, w jakiej
 * zakładka rysuje kolumny bez zapisanego układu). `id` = slug pola — pod nim układ trzyma
 * widoczność/szerokość/etykietę; pole wymagane jest kolumną wymaganą (nie da się jej ukryć),
 * a pole, z którego wiązanie `record_field` innego pola czyta parametr słownika, kolumną
 * zablokowaną (`locked`, też nie do ukrycia).
 * Domyślne wyrównanie to lewo dla każdej kolumny — tak tabela dodatkowa wyglądała przed
 * układem kolumn; prawo/środek ustawia się w układzie jawnie (edytor i karta liczą to samo).
 */
export function additionalTableDescriptors(
  fields: readonly AdditionalField[],
): EmbeddedTableColumnDescriptor[] {
  const dependencySlugs = recordFieldDependencySlugs(fields);
  return fields
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((field) => {
      const descriptor: EmbeddedTableColumnDescriptor = {
        id: field.slug,
        label: field.name,
        kind: columnKindOf(field.fieldType),
        required: field.isRequired,
        defaultAlign: 'left',
      };
      if (dependencySlugs.has(field.slug)) descriptor.locked = true;
      return descriptor;
    });
}
